import { useCallback, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Avatar } from "mastodon/components/avatar";
import { DisplayName } from "mastodon/components/display_name";
import { Search } from "mastodon/features/compose/components/search";
import ComposeFormContainer from "mastodon/features/compose/containers/compose_form_container";
import {
  mountCompose,
  unmountCompose,
  changeComposing,
  cancelReplyCompose,
} from "mastodon/actions/compose";
import { openModal } from "mastodon/actions/modal";
import { me } from "mastodon/initial_state";
import { useAppDispatch, useAppSelector } from "mastodon/store";
import { selectUnreadNotificationGroupsCount } from "mastodon/selectors/notifications";
import { DriscollHomeContext, accountHandle } from "./home-context";
import "./home.css";

const paths = {
  home: "m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z",
  bell: "M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 21h6",
  bookmark: "M6 3h12v18l-6-4-6 4Z",
  star: "m12 3 3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1Z",
  bubble: "M20 11.5a8 8 0 0 1-8 8H5l-3 3v-11a9 9 0 0 1 18 0ZM7 10h8M7 14h5",
  globe:
    "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM16 12c0 5-2 9-4 9s-4-4-4-9 2-9 4-9 4 4 4 9ZM3 12h18",
  people:
    "M12 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM3 21v-3a6 6 0 0 1 12 0v3M16 4a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 4v3",
  trend: "m3 17 6-6 4 4 8-10M15 5h6v6",
  list: "M4 3h16v18H4ZM8 8h8M8 12h8M8 16h8",
  tag: "m9 3-3 18M18 3l-3 18M3 8h18M2 16h18",
  collection: "m8 3 4 7H4ZM3 15h7v7H3ZM22 18a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
  settings:
    "M20 12a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM12 1v3M12 20v3M1 12h3M20 12h3M4 4l2 2M18 18l2 2M4 20l2-2M18 6l2-2",
};
export const DriscollIcon = ({ name }) => (
  <svg className="dr-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d={paths[name]} />
  </svg>
);

export const DriscollComposerAccount = () => {
  const account = useAppSelector((state) => state.accounts.get(me));
  if (!account) return null;
  return (
    <div className="dr-composer-account">
      <Link to={`/@${account.acct}`} className="dr-account-link">
        <Avatar account={account} size={36} />
        <span>
          <strong>
            <DisplayName account={account} variant="simple" />
          </strong>
          <small>{accountHandle(account)}</small>
        </span>
      </Link>
    </div>
  );
};

export const DriscollCancelReply = () => {
  const isReplying = useAppSelector((state) => !!state.compose.get("in_reply_to"));
  const dispatch = useAppDispatch();
  if (!isReplying) return null;
  return (
    <button
      type="button"
      className="dr-cancel-reply"
      onClick={() => dispatch(cancelReplyCompose())}
    >
      Cancel
    </button>
  );
};

const Header = ({ signedIn }) => {
  const account = useAppSelector((state) => state.accounts.get(me));
  const dispatch = useAppDispatch();
  const menuRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    if (menuRef.current) menuRef.current.open = false;
  }, [location.pathname]);
  useEffect(() => {
    const menu = menuRef.current;
    const fitMenu = () => {
      if (!menu?.open) return;
      const top = Math.ceil(menu.querySelector('nav').getBoundingClientRect().top);
      menu.style.setProperty('--dr-account-menu-top', `${top}px`);
    };
    menu?.addEventListener('toggle', fitMenu);
    window.addEventListener('resize', fitMenu);
    return () => {
      menu?.removeEventListener('toggle', fitMenu);
      window.removeEventListener('resize', fitMenu);
    };
  }, [signedIn, account]);
  useEffect(() => {
    const dismissOutside = (event) => {
      const menu = menuRef.current;
      if (menu?.open && !menu.contains(event.target)) menu.open = false;
    };
    const dismissEscape = (event) => {
      const menu = menuRef.current;
      if (event.key === "Escape" && menu?.open) {
        menu.open = false;
        menu.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissEscape);
    };
  }, []);
  return (
    <header className="dr-topbar">
      <div className="dr-topbar-inner">
        <a className="dr-brand" href="/" aria-label="driscoll.co home">
          <img src="/driscoll/v11/logo.jpg" alt="" />
          <span>
            <strong>
              driscoll<span className="dr-brand-dot">.</span>co
            </strong>
            <small>social.driscoll.co</small>
          </span>
        </a>
        <div className="dr-header-search">
          <Search singleColumn />
        </div>
        {signedIn && <Link className="button dr-new-post" to="/publish">New post</Link>}
        {!signedIn && <a className="button" href="/auth/sign_in">Sign in</a>}
        {signedIn && account && (
          <details className="dr-user-menu" ref={menuRef}>
            <summary aria-label="Account menu">
              <Avatar account={account} size={30} />
              <span className="dr-user-label">
                <DisplayName account={account} variant="simple" />
              </span>
              <svg className="dr-user-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="m4 6 4 4 4-4" />
              </svg>
            </summary>
            <nav aria-label="Your account">
              <a href={`/@${account.acct}`}>Your profile</a>
              <a href="/settings/preferences">Preferences</a>
              <Link to="/search">Search</Link>
              <Link to="/follow_requests">Follow requests</Link>
              <Link to="/blocks">Blocked accounts</Link>
              <Link to="/domain_blocks">Blocked servers</Link>
              <Link to="/mutes">Muted accounts</Link>
              <Link to="/keyboard-shortcuts">Keyboard shortcuts</Link>
              <Link to="/directory">Discover people</Link>
              <Link to="/lists">Lists</Link>
              <Link to="/followed_tags">Followed hashtags</Link>
              <Link to={`/@${account.acct}/collections`}>Collections</Link>
              <a href="/local">driscoll.co posts</a>
              <a href="/all">All posts</a>
              <a href="/trending">Trending</a>
              <a href="/">Public homepage</a>
              <button type="button" onClick={() => dispatch(openModal({modalType: "CONFIRM_LOG_OUT", modalProps: {}}))}>Sign out</button>
            </nav>
          </details>
        )}
      </div>
    </header>
  );
};

const spaceLinks = [
  ["/home", "home", "Home"],
  ["/notifications", "bell", "Notifications"],
  ["/bookmarks", "bookmark", "Bookmarks"],
  ["/favourites", "star", "Favourites"],
  ["/conversations", "bubble", "Private mentions"],
  ["/search", "globe", "Search"],
];
const feedLinks = [
  ["/local", "globe", "driscoll.co"],
  ["/all", "people", "All"],
  ["/trending", "trend", "Trending"],
];
const utilityLinks = [
  ["/lists", "list", "Lists"],
  ["/followed_tags", "tag", "Followed hashtags"],
  ["/collections", "collection", "Collections"],
  ["/settings/preferences", "settings", "Preferences"],
];

const Navigation = ({ signedIn }) => {
  const unread = useAppSelector(selectUnreadNotificationGroupsCount);
  const account = useAppSelector((state) => state.accounts.get(me));
  const renderLinks = (links, publicRoutes = false) =>
    links.map(([to, icon, label]) => {
      // Guests use the public Rails pages; signed-in feeds stay in the React app.
      if ((publicRoutes && !signedIn) || to.startsWith("/settings/"))
        return (
          <a href={to} key={to} aria-label={label}>
            <DriscollIcon name={icon} />
            <span>{label}</span>
          </a>
        );
      const target = to === "/collections" && account ? `/@${account.acct}/collections` : to;
      return (
        <NavLink key={to} to={target} exact activeClassName="dr-active" aria-label={label}>
          <DriscollIcon name={icon} />
          <span>{label}</span>
          {to === "/notifications" && unread > 0 && <span className="dr-unread">{unread}</span>}
        </NavLink>
      );
    });
  return (
    <aside className="dr-sidebar">
      <nav id="skip-link-target-nav" aria-label="Main" tabIndex={-1}>
        {signedIn && <>
          <p className="dr-sidebar-label">Your space</p>
          <div className="dr-nav dr-space-nav">{renderLinks(spaceLinks)}</div>
          <hr />
        </>}
        <p className="dr-sidebar-label">View posts</p>
        <div className="dr-nav dr-feed-nav">{renderLinks(feedLinks, true)}</div>
        {signedIn && <>
          <hr />
          <div className="dr-nav dr-utility-nav">{renderLinks(utilityLinks)}</div>
        </>}
      </nav>
    </aside>
  );
};

export const DriscollComposer = ({ autoFocus = false, manageMount = true }) => {
  const dispatch = useAppDispatch();
  const mounted = useAppSelector((state) => state.compose.get("mounted"));
  const handleFocus = useCallback(() => dispatch(changeComposing(true)), [dispatch]);
  useEffect(() => {
    if (!manageMount) return undefined;
    dispatch(mountCompose());
    return () => {
      dispatch(unmountCompose());
    };
  }, [dispatch, manageMount]);
  return (
    <section className="dr-composer" aria-label="Create a post" onFocus={handleFocus}>
      {mounted > 1 ? (
        <p>The composer is open in another panel.</p>
      ) : (
        <ComposeFormContainer singleColumn driscoll autoFocus={autoFocus} />
      )}
    </section>
  );
};

// All routed screens use the same real React structure; only Home adds the
// inline composer. Redux draft state and the standalone composer remain native.
export const DriscollApplicationShell = ({ children, signedIn = true, advanced = false }) => {
  const { pathname } = useLocation();
  const isHome = signedIn && !advanced && ["/home", "/timelines/home"].includes(pathname);
  useEffect(() => {
    document.body.classList.add("driscoll-react-home");
    return () => document.body.classList.remove("driscoll-react-home");
  }, []);
  return (
    <DriscollHomeContext.Provider value={!advanced}>
      <div className={`dr-home-app${advanced ? " dr-advanced-app" : ""}${!signedIn ? " dr-guest-app" : ""}`} data-driscoll-screen={pathname}>
        <Header signedIn={signedIn} />
        <div className="dr-shell">
          <Navigation signedIn={signedIn} />
          <main className="dr-feed" id={isHome ? "skip-link-target-content-1" : undefined} tabIndex={-1}>
            {isHome && <DriscollComposer />}
            {children}
          </main>
        </div>
      </div>
    </DriscollHomeContext.Provider>
  );
};

export const DriscollHomeShell = DriscollApplicationShell;
